/**
 * Admin — top-level admin container. R2: uses AdminShell.
 * Renders `AdminOverview` (KPI cards) when the URL is exactly /admin,
 * else the nested <Outlet /> from React Router.
 */
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ArrowUpRight, Users, ArrowDownToLine, ArrowUpFromLine, MessageSquare, IdCard, ChevronRight } from "lucide-react";

import AdminShell from "../components/ui-r1/AdminShell";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";
import api from "../lib/api";

export default function Admin() {
  const loc = useLocation();
  const isOverview = loc.pathname === "/admin" || loc.pathname === "/admin/";
  return (
    <AdminShell pageTitle={isOverview ? "Overview" : ""}>
      {isOverview ? <AdminOverview /> : <Outlet />}
    </AdminShell>
  );
}

function AdminOverview() {
  const [k, setK] = useState(null);
  useEffect(() => {
    let m = true;
    api.get("/admin/kpis").then(({ data }) => m && setK(data)).catch(() => {});
    return () => { m = false; };
  }, []);

  return (
    <div className="max-w-[1440px] mx-auto space-y-8" data-testid="admin-overview">
      <div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-2">Overview</div>
        <h1 className="font-display italic text-3xl sm:text-4xl text-ink mb-2">Bra Game · Control room</h1>
        <p className="text-sm text-ink-2 max-w-2xl">
          Snapshot of the live business. Head to the sections in the sidebar for deeper drill-downs.
        </p>
      </div>

      {/* Top KPIs — 4-up gradient cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testid="kpi-players"
          label="Active players · 7d"
          value={k?.active_players_7d ?? "—"}
          icon={Users}
          iconTone="pink"
        />
        <StatCard
          testid="kpi-ggr"
          label="GGR · 24h"
          value={k ? `$${k.ggr["24h"].toFixed(2)}` : "—"}
          icon={ArrowUpRight}
          iconTone="gold"
        />
        <StatCard
          testid="kpi-pending-withdrawals"
          label="Pending withdrawals"
          value={k?.withdrawals["24h"]?.pending_count ?? "—"}
          icon={ArrowUpFromLine}
          iconTone="purple"
        />
        <StatCard
          testid="kpi-open-chats"
          label="Open chats"
          value={k?.chat_open_count ?? "—"}
          sub={k?.chat_unread_count ? `${k.chat_unread_count} unread` : null}
          icon={MessageSquare}
          iconTone="cyan"
        />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testid="kpi-signups" label="Signups · 30d" value={k?.signups["30d"] ?? "—"} icon={Users} iconTone="pink" small />
        <StatCard testid="kpi-kyc-queue" label="KYC queue" value={k?.kyc_queue_depth ?? "—"} icon={IdCard} iconTone="gold" small />
        <StatCard testid="kpi-deposits" label="Deposits · 30d" value={k ? `$${k.deposits["30d"].toFixed(2)}` : "—"} icon={ArrowDownToLine} iconTone="cyan" small />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickLink to="/admin/kpis" title="KPI dashboard" desc="Real-time GGR trend, RTP vs target, deposits by provider." />
        <QuickLink to="/admin/withdrawals" title="Withdrawal queue" desc="Approve / reject pending withdrawal requests." badge={(k?.withdrawals["24h"]?.pending_count ?? 0) > 0 ? String(k.withdrawals["24h"].pending_count) : null} badgeVariant="danger" />
        <QuickLink to="/admin/chat" title="Support chat" desc="Two-pane operator inbox with unread badges." badge={k?.chat_unread_count ? String(k.chat_unread_count) : null} badgeVariant="hot" />
      </div>
    </div>
  );
}

function StatCard({ testid, label, value, sub, icon: Icon, iconTone = "gold", small = false }) {
  const toneGradient = {
    gold: "from-gold/20 to-gold/5 border-gold/40",
    pink: "from-pink/20 to-pink/5 border-pink/40",
    purple: "from-purple/20 to-purple/5 border-purple/40",
    cyan: "from-[color:var(--success)]/20 to-[color:var(--success)]/5 border-[color:var(--success)]/40",
  };
  const iconColor = {
    gold: "text-gold",
    pink: "text-pink",
    purple: "text-purple",
    cyan: "text-[color:var(--success)]",
  };
  return (
    <div
      className={`rounded-xl border p-5 bg-gradient-to-br ${toneGradient[iconTone] || toneGradient.gold} relative overflow-hidden`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-2">{label}</div>
        {Icon && (
          <span className={`w-9 h-9 rounded-lg bg-highest/60 flex items-center justify-center ${iconColor[iconTone]}`}>
            <Icon className="w-4 h-4" />
          </span>
        )}
      </div>
      <div className={`font-display italic ${small ? "text-3xl" : "text-4xl"} text-ink leading-tight`}>{value}</div>
      {sub && <div className="text-[10px] tracking-widest uppercase text-danger mt-1">{sub}</div>}
    </div>
  );
}

function QuickLink({ to, title, desc, badge, badgeVariant = "muted" }) {
  return (
    <Link to={to}>
      <Card variant="glow">
        <CardBody className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display italic text-lg text-ink mb-1">{title}</div>
            <div className="text-xs text-ink-2 leading-relaxed">{desc}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {badge && <Badge variant={badgeVariant}>{badge}</Badge>}
            <ChevronRight className="w-4 h-4 text-ink-3" />
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}
